import Button, { LoadingButton } from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import { CommerceAddress, GraphEntity, TaxId } from '@css-xp/graphtypes/src/generated-types-new';
import { createTransactionAccountDetailsV2, updatePricingEligibility } from '@atlassian/temp-proxies';
import {
  CreateTreansactionAccountV2,
  FormattedResponseFromCCP,
  setPricingEligibility,
  UpdateTransactionAccountPayload,
} from '@atlassian/cssxp-types-billing';
import {
  ErrorComponents,
  getFallbackTaxId,
  getRouteFromDirectMatch,
  getTaxIds,
  InfoPanel,
  IsoSelectItem,
  NotificationTypes,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  useUpdateTransactionAccount,
} from '@css-xp/base';
import defaultTo from 'lodash/defaultTo';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
// since these are controlled components, please do the data validation beforehand
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { AddBillingAdminsComponent, BillingAddressComponent, PaymentMethodComponent } from './ceate-txa-components';

export interface FormData {
  billToName?: string | null;
  billToAddress1?: string | null;
  billToAddress2?: string | null;
  billToCity?: string | null;
  billToCountry?: IsoSelectItem | null;
  billToPostalCode?: string | null;
  billToState?: IsoSelectItem | null;
  billToTaxId?: string | null;
  billToGst?: string | null;
  billToPst?: string | null;
  billToTaxIds?: TaxId[] | null;

  shipToName?: string | null;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: IsoSelectItem | null;
  shipToPostalCode?: string | null;
  shipToState?: IsoSelectItem | null;
  shipToTaxId?: string | null;

  billingAdmin?: string;
  recipients?: string[];
  pricingPlan?: string;
  paymentMethod?: string | null;
}
/**
 * This component is to dispaly sidebar to create transaction account
 * */
enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

enum FormTrackerStep {
  BILLING_ADMIN_STEP = 0,
  BILLING_ADDRESS_STEP,
  PAYMENT_METHOD_STEP,
}

enum FormStages {
  BILLING_ADMIN_STEP = 'Billing admin',
  BILLING_ADDRESS_STEP = 'Billing address & Pricing eligibility',
  PAYMENT_METHOD_STEP = 'Payment methods',
}

const fieldEmptyValidationError = {
  CITY: '"billToAddress.postalAddress.city"',
  ADDRESS1: '"billToAddress.postalAddress.line1"',
  COUNTRY: '"billToAddress.postalAddress.country"',
  POSTCODE: '"billToAddress.postalAddress.postcode"',
  COMPANYNAME: '"billToAddress.name"',
};

function getErrorMessages(message: string) {
  if (message.indexOf(fieldEmptyValidationError.CITY) === 0) return 'City is not allowed to be empty';
  if (message.indexOf(fieldEmptyValidationError.COUNTRY) === 0) return 'Country is not allowed to be empty';
  if (message.indexOf(fieldEmptyValidationError.POSTCODE) === 0) return 'Postal Code is not allowed to be empty';
  if (message.indexOf(fieldEmptyValidationError.ADDRESS1) === 0) return 'Address 1 is not allowed to be empty';
  if (message.indexOf(fieldEmptyValidationError.COMPANYNAME) === 0) return 'Company name is not allowed to be empty';
  else return message;
}

export const CreateTransactionAccountSidebar = () => {
  const [currentProgressIndex, setCurrentProgressIndex] = React.useState<number>(0);
  const [txaccount, setTxaFormData] = React.useState<FormData>({});
  // const [isChecked, setIsChecked] = React.useState<boolean>(false);
  const [newShipToAddress, setShipToAddress] = React.useState<CommerceAddress>();
  // To track if account creation is pending in BillingAdminsFields and save button should be disabled.
  const [disableSaveButton, setDisableSaveButton] = React.useState<boolean>(false);
  const [isBillToComplete, setBillToComplete] = React.useState<boolean>(false);
  const [errMsgList, setErrMsgList] = React.useState<string>();
  const history = useHistory();
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { closeSidebar } = useSidebar();

  const onCreateTransactionAccountComplete = useCallback(
    (responsePayload) => {
      const notificationOptions = successNotificationBuilder()
        .withId('create-transaction-account-success')
        .withHeader('Successfully created transaction account.')
        .withMessage(
          `Created billing account ${
            responsePayload?.transactionData?.number ?? responsePayload?.data
          }. You can add other details or skip for now.`
        )
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();

      sendSuccessNotification(notificationOptions);

      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.CREATED,
        actionSubject: AnalyticsTrackSubject.TRANSACTION_ACCOUNT,
      });
      // reset values for notification undefined
      setTimeout(() => {
        sendSuccessNotification(undefined);
        setNotificationSent(false);
      }, 100);
    },
    [sendSuccessNotification, setNotificationSent]
  );

  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    CreateTreansactionAccountV2,
    any
  >({
    submitFormAction: createTransactionAccountDetailsV2,
    onComplete: onCreateTransactionAccountComplete,
  });

  const onCancelForm = () => {
    if (currentProgressIndex === 1) {
      setCurrentProgressIndex(currentProgressIndex + 1);
    } else {
      triggerNotificationFlag();
    }
  };

  const triggerNotificationFlag = () => {
    // For non complete, we should not trigger notification
    // For second step, if we click skip, then notification should not triggered
    if (complete && currentProgressIndex !== 1 && !notificationSent) {
      const notificationOptions = successNotificationBuilder()
        .withId('update-transaction-account-success')
        .withHeader('Successfully updated transaction account.')
        .withMessage(`Saved details for billing account ${response?.transactionData?.number ?? response?.data}`)
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();

      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.TRANSACTION_ACCOUNT,
      });

      // Close sidebar after sending the notification
      setTimeout(() => {
        closeSidebar();
        history.push(
          getRouteFromDirectMatch(
            {
              entity: GraphEntity.TRANSACTION_ACCOUNT,
              identifier: response?.data,
            },
            true
          )
        );
      }, 100);
    } else {
      closeSidebar();
    }
  };

  const errorMsgList = (errList?: FormattedResponseFromCCP[]) => {
    const errMsg = errList?.map((error) => {
      let errorMessages = [];
      if (error.error?.payload?.validationErrors) {
        errorMessages = error.error?.payload?.validationErrors.map((message: any) => `${message?.validationMessage}`);
      } else {
        errorMessages.push(error.error?.message ? getErrorMessages(error.error.message) : '');
      }
      return errorMessages;
    });
    setErrMsgList(errMsg?.join(','));
    return;
  };

  const { isProcessing: isSubmittingBillTo, updateTransactionAccount } = useUpdateTransactionAccount({
    onClose: () => {
      //do nothing
    },
    onComplete: () => {
      setBillToComplete(true);
      setErrMsgList('');
    },
    sendNotification: false,
    errorMsgList,
  });

  const {
    error: pricingError,
    isProcessing: isSubmittingPricing,
    submitForm: submitPricingEligibility,
    complete: isUpdatePricingComplete,
  } = useFormSubmit<setPricingEligibility, any>({
    submitFormAction: updatePricingEligibility,
  });

  const stages = Object.values(FormStages);

  /*
  // Removing the following codeblock as it is not used in the component it is passed to
  const onSameAsBillTo = (isCheckboxEnabled: boolean, billingData: FormData) => {
    const formBillingAddress = {
      name: billingData.billToName,
      addressLine1: billingData.billToAddress1,
      addressLine2: billingData.billToAddress2,
      city: billingData.billToCity,
      state: billingData.billToState?.value,
      postcode: billingData.billToPostalCode,
      taxId: billingData.billToTaxId,
      country: billingData.billToCountry?.value,
    } as CommerceAddress;

    setIsChecked(isCheckboxEnabled);
    if (isCheckboxEnabled) {
      setShipToAddress(formBillingAddress);
    }
  };
  */

  const formatBillToAdress = (): CommerceAddress => {
    return {
      name: defaultTo(txaccount.billToName || null, ''),
      addressLine1: defaultTo(txaccount.billToAddress1 || null, ''),
      addressLine2: defaultTo(txaccount.billToAddress2 || null, null),
      city: txaccount?.billToCity,
      state: defaultTo(txaccount?.billToState?.value || null, null),
      postcode: txaccount?.billToPostalCode ?? '',
      taxId: txaccount?.billToTaxId || null,
      taxIds: txaccount?.billToTaxIds || null,
      country: txaccount?.billToCountry?.value ?? '',
    } as CommerceAddress;
  };

  const onSubmitBillTo = (billToValues: FormData) => {
    if (!billToValues.billToName) {
      setErrMsgList('Company name is not allowed to be empty');
      return;
    }
    if (billToValues.billToCountry && !billToValues.billToState) {
      setErrMsgList('State is not allowed to be empty');
      return;
    }

    const taxIds = getTaxIds(billToValues);

    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      billToAddress: {
        name: defaultTo(billToValues.billToName || null, ''),
        postalAddress: {
          line1: defaultTo(billToValues.billToAddress1 || null, ''),
          line2: defaultTo(billToValues.billToAddress2 || null, null),
          city: billToValues?.billToCity ?? '',
          state: defaultTo(billToValues?.billToState?.value || null, null),
          postcode: billToValues?.billToPostalCode ?? '',
          country: billToValues?.billToCountry?.value ?? '',
        },
        taxId: billToValues?.billToTaxId || getFallbackTaxId(taxIds),
        taxIds: taxIds?.length ? taxIds : null,
      },
    };
    updateTransactionAccount({ transactionAccountId: response?.data, updateTransactionAccountPayload });
  };

  const submitBillingDetails = (billingData: FormData) => {
    setTxaFormData({ ...txaccount, ...billingData });
    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_ADMIN_STEP: {
        const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {};
        const billingAdmins = [];
        for (const key in billingData) {
          if (key.includes('recipients-')) {
            // @ts-ignore
            billingAdmins.push(billingData[key]);
          }
        }
        if (billingAdmins.length > 0) {
          updateTransactionAccountPayload.billingAdmin = billingAdmins[0];
          if (billingAdmins.length > 1) {
            updateTransactionAccountPayload.modifiedBillingAdmins = {
              emailsOrAaidsToAdd: billingAdmins,
              emailsOrAaidsToDelete: [],
            };
          }
        }
        submitForm(updateTransactionAccountPayload);
        break;
      }

      case FormTrackerStep.BILLING_ADDRESS_STEP: {
        if (isBillToComplete) {
          submitPricingEligibility({
            pricingEligibility: { [`${billingData.pricingPlan}`]: 'true' },
            transactionAccountId: response?.data,
          });
        } else {
          if (currentProgressIndex < stages.length - 1) {
            setCurrentProgressIndex(currentProgressIndex + 1);
            return;
          }
        }
        break;
      }

      case FormTrackerStep.PAYMENT_METHOD_STEP: {
        triggerNotificationFlag();
        break;
      }
    }
  };

  if (complete) {
    if (currentProgressIndex < FormTrackerStep.BILLING_ADDRESS_STEP) {
      setCurrentProgressIndex(currentProgressIndex + 1);
    }
  }

  if (isUpdatePricingComplete) {
    if (currentProgressIndex < FormTrackerStep.PAYMENT_METHOD_STEP) {
      setCurrentProgressIndex(currentProgressIndex + 1);
    }
  }

  const getFormStep = (props: any) => {
    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_ADMIN_STEP: {
        const billingAdmins = [];
        for (const key in txaccount) {
          if (key.includes('recipients')) {
            // @ts-ignore
            billingAdmins.push(txaccount[key]);
          }
        }
        return <AddBillingAdminsComponent recipients={billingAdmins} setDisableSaveButton={setDisableSaveButton} />;
      }
      case FormTrackerStep.BILLING_ADDRESS_STEP:
        return (
          <BillingAddressComponent
            {...props}
            // onSameAsBillTo={onSameAsBillTo}
            billToAddress={formatBillToAdress()}
            isChecked={false}
            newShipToAddress={newShipToAddress}
            pricingEligibility={txaccount.pricingPlan}
            canSelectPricing={isBillToComplete}
          />
        );
      case FormTrackerStep.PAYMENT_METHOD_STEP:
        return <PaymentMethodComponent transactionAccountId={response?.data} />;
      default:
        return null;
    }
  };

  const items: Stages = stages.map((stage, index) => ({
    id: `${index}`,
    label: stage,
    percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
    status:
      currentProgressIndex > index
        ? TrackerState.VISITED
        : currentProgressIndex === index
        ? TrackerState.CURRENT
        : TrackerState.UNVISITED,
    // onClick: () => setCurrentProgressIndex(index),
  }));

  const getSubmitCTALabel = () => {
    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_ADMIN_STEP:
        return 'Create billing account & Continue';
      case FormTrackerStep.BILLING_ADDRESS_STEP:
        return 'Save & Continue';
      default:
        return 'Save & exit';
    }
  };

  const getCancelCTALabel = () => {
    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_ADMIN_STEP:
        return 'Cancel';
      case FormTrackerStep.PAYMENT_METHOD_STEP:
        return 'Skip & exit';
      default:
        return 'Skip';
    }
  };

  return (
    <div className="pane-sidebar-content" style={{ padding: '25px' }}>
      <Form onSubmit={submitBillingDetails}>
        {({ formProps, submitting, ...props }) => (
          <form {...formProps}>
            <div className="header">
              <FormHeader title="Create billing account"></FormHeader>
            </div>

            <div className="progress-content">
              <ProgressTracker items={items} spacing="compact" />
            </div>

            <FormSection>
              {(formError || pricingError || errMsgList) && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to create billing account for the following reason"
                      message={formError || pricingError || errMsgList || 'Something went wrong'}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>{getFormStep({ onSubmitBillTo, isSubmittingBillTo, ...props })}</FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={onCancelForm} testId="cancel-button">
                  {getCancelCTALabel()}
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isDisabled={disableSaveButton}
                  isLoading={isProcessing || isSubmittingPricing}
                  testId={submitting || false ? 'loading-button' : 'save-button'}
                >
                  {getSubmitCTALabel()}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
